import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Review {
  id: string;
  author: string;
  country: string;
  rating: number;
  content: string;
  date: string;
}

interface ReviewSectionProps {
  reviews: Review[];
  tabLabel: string;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-xs ${
            star <= rating
              ? 'ri-star-fill text-warm-500'
              : 'ri-star-line text-ink-200'
          }`}
        />
      ))}
    </div>
  );
}

export default function ReviewSection({ reviews, tabLabel }: ReviewSectionProps) {
  const { t } = useTranslation('products');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const avgRating =
    reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

  if (reviews.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 rounded-full bg-cream-100 flex items-center justify-center mb-4">
          <i className="ri-chat-3-line text-2xl text-ink-300" />
        </div>
        <h4 className="text-base font-bold text-ink-600 mb-1">{t('detail.noReviews')}</h4>
        <p className="text-sm text-ink-400">{t('detail.noReviewsDesc')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 p-4 bg-cream-50/70 rounded-lg border border-ink-100">
        <div className="text-center sm:text-left">
          <div className="text-3xl font-bold text-ink-700">{avgRating.toFixed(1)}</div>
          <StarRating rating={Math.round(avgRating)} />
          <p className="text-xs text-ink-400 mt-1">{t('detail.reviewCount', { count: reviews.length })}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = reviews.filter((r) => r.rating === star).length;
            const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-1.5 text-xs w-full sm:w-auto">
                <span className="text-ink-400 w-4 text-right">{star}</span>
                <i className="ri-star-fill text-warm-500 text-[10px]" />
                <div className="w-20 h-1.5 bg-cream-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-warm-400 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-ink-400 w-5">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Review List */}
      <div className="space-y-3">
        {reviews.map((review) => {
          const isExpanded = expandedId === review.id;
          const isLong = review.content.length > 120;
          return (
            <div
              key={review.id}
              className="p-4 rounded-lg border border-ink-100 bg-white"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-cream-200 flex items-center justify-center text-xs font-bold text-ink-500">
                      {review.author.charAt(0)}
                    </div>
                    <span className="text-sm font-semibold text-ink-700">{review.author}</span>
                    <span className="text-[10px] text-ink-400 bg-cream-50 px-1.5 py-0.5 rounded border border-ink-100">
                      {review.country}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRating rating={review.rating} />
                    <span className="text-[11px] text-ink-300">{review.date}</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-ink-500 leading-relaxed">
                {isLong && !isExpanded
                  ? `${review.content.slice(0, 120)}...`
                  : review.content}
              </p>
              {isLong && (
                <button
                  onClick={() =>
                    setExpandedId(isExpanded ? null : review.id)
                  }
                  className="text-xs text-warm-500 font-medium mt-1 hover:underline cursor-pointer"
                >
                  {isExpanded ? t('detail.collapse') : t('detail.expand')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}